import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { useParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { useOrderTicket, useGetAllTickets } from "../lib/api/ticket";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

const tiers = [
  {
    slug: "final-release",
    name: "Final Release Ticket",
    price: 130,
    description: "Exclusive benefits for a premium evening.",
    features: ["Welcome Drink", "Five-course Dinner With Hosted Pairings"],
  },
];

export default function TicketCheckoutPage() {
  const params = useParams();
  const { id } = params;
  const tier = tiers.find((t) => t.slug === id);

  const [quantity, setQuantity] = useState(1);
  const [attendees, setAttendees] = useState([
    { firstName: "", lastName: "", email: "", dietaryRequirements: "" },
  ]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const { data: ticketsData, isLoading: ticketsLoading } = useGetAllTickets();
  const { mutateAsync: orderTicket, isPending } = useOrderTicket();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  if (!tier) {
    return (
      <div className="h-screen flex items-center justify-center text-xl font-medium">
        Ticket option not found.
      </div>
    );
  }

  if (ticketsLoading) {
    return (
      <div className="h-screen flex items-center justify-center text-lg font-medium">
        Checking ticket availability...
      </div>
    );
  }

  // Get count of already paid tickets
  const paidTicketsCount =
    ticketsData?.data?.filter(
      (ticket: any) => ticket.orderId.paymentStatus === "paid"
    )?.length || 0;

  const maxTicketsAllowed = 40;
  // const remainingSpots = maxTicketsAllowed - paidTicketsCount;
  const remainingSpots = 0;

  // --- Handle Quantity Change ---
  const handleQuantityChange = (newQuantity: number) => {
    setErrorMsg("");

    // Prevent buying more than 2 at once
    if (newQuantity > 2) {
      setErrorMsg("You can only purchase up to 2 tickets per transaction.");
      return;
    }

    // Prevent exceeding total limit
    if (newQuantity > remainingSpots) {
      // setErrorMsg(
      //   `${remainingSpots} ticket${remainingSpots === 1 ? "" : "s"} remaining.`
      // );
      setErrorMsg(`Tickets are sold out!!!`);
      return;
    }

    setQuantity(newQuantity);
    setAttendees((prev) => {
      const updated = [...prev];
      if (newQuantity > updated.length) {
        for (let i = updated.length; i < newQuantity; i++) {
          updated.push({
            firstName: "",
            lastName: "",
            email: "",
            dietaryRequirements: "",
          });
        }
      } else {
        updated.length = newQuantity;
      }
      return updated;
    });
  };

  // --- Handle Attendee Field Change ---
  const handleAttendeeChange = (
    index: number,
    field: string,
    value: string
  ) => {
    const updated = [...attendees];
    (updated[index] as any)[field] = value;
    setAttendees(updated);
  };

  // --- Submit Handler ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");

    if (paidTicketsCount >= maxTicketsAllowed) {
      setErrorMsg("All tickets have been sold out.");
      return;
    }

    if (quantity > remainingSpots) {
      // setErrorMsg(
      //   `Only ${remainingSpots} ticket${remainingSpots === 1 ? "" : "s"} remaining.`
      // );

      setErrorMsg(`Tickets are sold out!!!`);
      return;
    }

    if (quantity > 2) {
      setErrorMsg("You can only buy up to 2 tickets at a time.");
      return;
    }

    try {
      setLoading(true);
      const res = await orderTicket({
        tier: tier.slug,
        quantity,
        attendees,
      });

      const stripe = await stripePromise;
      if (!stripe) throw new Error("Stripe failed to load");

      const result = await stripe.redirectToCheckout({
        sessionId: res?.data?.id,
      });

      if (result?.error) {
        console.error(result.error.message);
      }
    } catch (error) {
      console.error("Payment failed:", error);
      setErrorMsg("Something went wrong while processing your order.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-gradient-to-b from-[#f1f1f1] to-[#F5F3F0] py-20 px-6 md:px-12 flex items-center">
      <div className="max-w-6xl mx-auto bg-white/90 backdrop-blur-md border-4 border-[#B9A68D] shadow-xl py-10 px-5 grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Ticket Summary */}
        <div className="mb-10 lg:mb-0 text-center lg:text-left">
          <h2 className="text-4xl md:text-5xl font-serif text-[#4E5C36] tracking-wide mb-3">
            {tier.name}
          </h2>
          <p className="text-3xl font-bold text-gray-900 mb-2">£{tier.price}</p>
          <p className="text-gray-700 text-lg italic">{tier.description}</p>

          <ul className="mt-6 space-y-3 text-gray-800 text-left">
            {tier.features.map((feature, i) => (
              <li
                key={i}
                className="flex items-center gap-3 border-b border-gray-200 pb-2"
              >
                <Check className="h-5 w-5 text-[#4E5C36]" />
                <span className="text-base">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Form Section */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {errorMsg && (
            <div className="p-3 text-red-700 bg-red-100 border border-red-300 rounded-md text-sm font-medium">
              {errorMsg}
            </div>
          )}

          {/* Ticket Quantity */}
          <div>
            <label className="block font-medium text-gray-900 mb-2 text-lg">
              Number of Tickets
            </label>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => handleQuantityChange(Math.max(1, quantity - 1))}
                className="px-4 py-2 bg-gray-200 text-gray-800 text-lg font-semibold rounded hover:bg-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={quantity <= 1}
              >
                −
              </button>

              <span className="w-12 text-center text-lg font-medium text-gray-900">
                {quantity}
              </span>

              <button
                type="button"
                onClick={() => handleQuantityChange(quantity + 1)}
                className="px-4 py-2 bg-gray-200 text-gray-800 text-lg font-semibold rounded hover:bg-gray-300 transition"
              >
                +
              </button>
            </div>
            {/* <p className="text-sm text-gray-600 mt-2 italic">
              {remainingSpots} ticket{remainingSpots === 1 ? "" : "s"} left
            </p> */}
          </div>

          {/* Attendee Info */}
          {attendees.map((attendee, index) => (
            <div
              key={index}
              className="p-4 border border-gray-200 rounded-md bg-gray-50 space-y-4"
            >
              {quantity > 1 && (
                <h3 className="font-semibold text-[#4E5C36]">
                  Attendee {index + 1}
                </h3>
              )}
              <div>
                <label className="block font-medium text-gray-900 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  value={attendee.firstName}
                  onChange={(e) =>
                    handleAttendeeChange(index, "firstName", e.target.value)
                  }
                  required
                  className="w-full px-4 text-base py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#4E5C36] focus:border-[#4E5C36] transition bg-white/80 "
                />
              </div>

              <div>
                <label className="block font-medium text-gray-900 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  value={attendee.lastName}
                  onChange={(e) =>
                    handleAttendeeChange(index, "lastName", e.target.value)
                  }
                  required
                  className="w-full px-4 text-base py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#4E5C36] focus:border-[#4E5C36] transition bg-white/80 "
                />
              </div>

              <div>
                <label className="block font-medium text-gray-900 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={attendee.email}
                  onChange={(e) =>
                    handleAttendeeChange(index, "email", e.target.value)
                  }
                  required
                  className="w-full px-4 text-base py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#4E5C36] focus:border-[#4E5C36] transition bg-white/80 "
                />
              </div>

              <div>
                <label className="block font-medium text-gray-900 mb-2">
                  Dietary Requirements
                </label>
                <input
                  type="text"
                  value={attendee.dietaryRequirements}
                  onChange={(e) =>
                    handleAttendeeChange(
                      index,
                      "dietaryRequirements",
                      e.target.value
                    )
                  }
                  placeholder="e.g. Vegetarian, Gluten-free"
                  className="w-full px-4 text-base py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#4E5C36] focus:border-[#4E5C36] transition bg-white/80 "
                />
              </div>
            </div>
          ))}

          {/* Submit Button */}
          <button
            type="submit"
            // disabled={isPending || loading || remainingSpots < 1}
            disabled={true}
            className="w-full py-4 font-semibold text-white text-lg tracking-wide shadow-md transition bg-[#4E5C36] hover:bg-[#3b452a] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Fully Booked
            {/* {isPending || loading ? "Redirecting..." : "Secure Booking"} */}
          </button>
        </form>
      </div>
    </section>
  );
}
